import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { registerInput } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/shared/field';
import { api, errorMessage } from '@/lib/api';
import { meKey } from '@/lib/session';
import { TermsConsentField } from '@/features/legal/terms-consent';
import { AuthLayout } from './auth-layout';
import { FormError } from './form-error';

type Values = z.input<typeof registerInput>;

export function RegisterPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(registerInput),
    defaultValues: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo' },
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.post('/auth/register', values);
      await qc.invalidateQueries({ queryKey: meKey });
      navigate('/app/primeiros-passos', { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  return (
    <AuthLayout
      title="Cadastre sua barbearia"
      subtitle="Leva um minuto. Depois você configura serviços, equipe e o site."
      footer={
        <>
          Já tem conta?{' '}
          <Link to="/entrar" className="font-medium text-primary underline-offset-4 hover:underline">
            Entrar
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        <FormError message={error} />
        <Field id="organizationName" label="Nome da barbearia" error={errors.organizationName?.message}>
          <Input id="organizationName" autoComplete="organization" {...form.register('organizationName')} />
        </Field>
        <Field id="name" label="Seu nome" error={errors.name?.message}>
          <Input id="name" autoComplete="name" {...form.register('name')} />
        </Field>
        <Field id="email" label="E-mail" error={errors.email?.message}>
          <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
        </Field>
        <Field id="password" label="Senha" error={errors.password?.message} hint="Mínimo de 8 caracteres, com letras e números.">
          <Input id="password" type="password" autoComplete="new-password" {...form.register('password')} />
        </Field>
        <TermsConsentField inputProps={form.register('acceptTerms')} error={errors.acceptTerms?.message} />
        <Button type="submit" size="lg" disabled={isSubmitting}>
          {isSubmitting ? 'Criando conta…' : 'Criar conta'}
        </Button>
      </form>
    </AuthLayout>
  );
}
